import { Project } from "graphql/_Types";
import {
  createProjectFolderDjango,
  deleteProjectFolderDjango,
  getProjectFoldersDjango,
  ProjectFolderDjango,
  updateProjectFolderDjango,
} from "./ProjectFoldersProvider.api";
import { createStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { getProject } from "../store/DataTableProvider.api";
import assert from "assert";
import { isDefined } from "utils/isDefined";

type CreateProjectFoldersOptions = {
  projectKey: Project["key"];
};

export type ProjectFoldersStoreState = {
  folders: ProjectFolderDjango[];
  getSortedFolders: () => ProjectFolderDjango[];
  setFolders: (folders: ProjectFolderDjango[]) => void;
  createFolder: (options: {
    name: string;
    color: string | null;
  }) => Promise<
    | { folder: ProjectFolderDjango; error: undefined }
    | { folder: undefined; error: Error }
  >;
  editFolder: (options: {
    folderId: ProjectFolderDjango["id"];
    name: string;
    color: string | null;
  }) => Promise<
    | { folder: ProjectFolderDjango; error: undefined }
    | { folder: undefined; error: Error }
  >;
  deleteFolder: (options: {
    folderId: ProjectFolderDjango["id"];
  }) => Promise<{ error: Error | undefined }>;
};

export const createProjectFoldersStore = async ({
  projectKey,
}: CreateProjectFoldersOptions) => {
  const project = await getProject({ projectKey });
  const folders = await getProjectFoldersDjango(project.id);

  return createStore<ProjectFoldersStoreState>()(
    immer((set, get) => {
      return {
        // initialize folders with the fetched data, sort alphabetically
        folders: [...folders].sort((a, b) => a.name.localeCompare(b.name)),

        // Memoized getter for sorted folders to avoid unnecessary re-sorting
        getSortedFolders: (() => {
          let lastFolders: ProjectFolderDjango[] | undefined;
          let lastSorted: ProjectFolderDjango[] | undefined;
          return () => {
            const folders = get().folders;
            // If folders reference hasn't changed, return cached sorted array
            if (lastFolders === folders && lastSorted) {
              return lastSorted;
            }
            // Otherwise, sort and cache the result
            lastFolders = folders;
            lastSorted = [...folders].sort((a, b) =>
              a.name.localeCompare(b.name),
            );
            return lastSorted;
          };
        })(),

        setFolders: (newFolders: ProjectFolderDjango[]) => {
          // set sorted list of new folders
          set({
            folders: [...newFolders].sort((a, b) =>
              a.name.localeCompare(b.name),
            ),
          });
        },
        createFolder: async ({ name, color }) => {
          try {
            const folder = await createProjectFolderDjango(
              name,
              project.id,
              color,
            );
            set((state) => {
              state.folders.push(folder);
            });
            return { folder };
          } catch (error) {
            return {
              error: new Error("Failed to create folder"),
            };
          }
        },

        editFolder: async ({ folderId, name, color }) => {
          try {
            const folderResult = await updateProjectFolderDjango(
              folderId,
              name,
              color,
            );
            // Update store state
            set((state) => {
              const folder = state.folders.find(({ id }) => id === folderId);
              assert(isDefined(folder));

              if (isDefined(name)) {
                folder.name = name;
              }

              if (isDefined(color)) {
                folder.color = color;
              }
            });
            return { folder: folderResult };
          } catch (error) {
            return { error: new Error("Failed to update folder") };
          }
        },
        deleteFolder: async ({ folderId }) => {
          try {
            const folder = get().folders.find(({ id }) => id === folderId);
            assert(isDefined(folder));

            await deleteProjectFolderDjango(folderId);
            set((state) => {
              state.folders = state.folders.filter(
                (folder) => folder.id !== folderId,
              );
            });
            return { error: undefined };
          } catch (error) {
            return { error: new Error("Failed to delete folder") };
          }
        },
      };
    }),
  );
};
